import {
    BarController,
    BarElement,
    CategoryScale,
    Chart,
    type ChartOptions,
    Legend,
    LinearScale,
    LogarithmicScale,
    Tooltip,
    type TooltipItem,
} from "chart.js";
import { isNullOrUndef } from "chart.js/helpers";
import { type TCountryCode, countries } from "countries-list";
import "../styles/nose-count.scss";

Chart.register(
    BarController,
    BarElement,
    CategoryScale,
    LinearScale,
    LogarithmicScale,
    Tooltip,
    Legend,
);
Chart.defaults.font.family = "Roboto, sans-serif";
Chart.defaults.color = "#fff";
Chart.defaults.borderColor = "#324b52";

const defaultBarOptions = {
    scales: {
        y: {
            beginAtZero: true,
            ticks: {
                autoSkipPadding: 30,
                autoSkip: true,
                callback: (tickValue) => ((tickValue as number) % 1 === 0 ? tickValue : undefined),
            },
        },
        x: {
            grid: {
                display: false,
            },
        },
    },
    maintainAspectRatio: false,
    plugins: {
        legend: {
            display: false,
        },
        tooltip: {
            callbacks: {
                label(tooltipItem: TooltipItem<"bar">): string {
                    let label = "";

                    if (this.options.mode === "dataset") {
                        label += `${tooltipItem.label}: `;
                    } else if (tooltipItem.dataset.label) {
                        label += `${tooltipItem.dataset.label}: `;
                    }

                    if (!isNullOrUndef(tooltipItem.formattedValue)) {
                        label += tooltipItem.formattedValue;
                    }

                    const currentValue = tooltipItem.dataset.data[tooltipItem.dataIndex];

                    if (typeof currentValue !== "number") {
                        return label;
                    }

                    const sum = tooltipItem.dataset.data.reduce<number>((sum, current) => {
                        if (typeof current !== "number") {
                            return sum;
                        }

                        return sum + current;
                    }, 0);
                    const percent = (currentValue / sum) * 100;

                    label += ` (${percent.toFixed(1)}%)`;

                    return label;
                },
            },
        },
    },
} satisfies ChartOptions<"bar">;

const renderTotal = (element: HTMLCanvasElement | null) => {
    if (!element) {
        return;
    }

    const entries = JSON.parse(element.dataset.statistics as string) as {
        paid: {
            attendees: number;
            fursuiters: number;
            silverSponsors: number;
            goldSponsors: number;
        };
        unpaid: {
            attendees: number;
            fursuiters: number;
            silverSponsors: number;
            goldSponsors: number;
        };
    };

    new Chart(element, {
        type: "bar",
        data: {
            labels: ["Total", "Fursuiters", "Silver Sponsors", "Gold Sponsors"],
            datasets: [
                {
                    label: "Registered",
                    data: [
                        entries.paid.attendees + entries.unpaid.attendees,
                        entries.paid.fursuiters + entries.unpaid.fursuiters,
                        entries.paid.silverSponsors + entries.unpaid.silverSponsors,
                        entries.paid.goldSponsors + entries.unpaid.goldSponsors,
                    ],
                    backgroundColor: "#0d7d9a",
                },
                {
                    label: "Confirmed",
                    data: [
                        entries.paid.attendees,
                        entries.paid.fursuiters,
                        entries.paid.silverSponsors,
                        entries.paid.goldSponsors,
                    ],
                    backgroundColor: "#1e6375",
                },
            ],
        },
        options: {
            ...defaultBarOptions,
            plugins: {
                ...defaultBarOptions.plugins,
                legend: {
                    display: true,
                },
            },
        },
    });
};

const renderCountryDistribution = (element: HTMLCanvasElement | null) => {
    if (!element) {
        return;
    }

    const entries = JSON.parse(element.dataset.statistics as string) as {
        code: TCountryCode;
        count: number;
    }[];

    entries.sort((a, b) => {
        const result = b.count - a.count;

        if (result !== 0) {
            return result;
        }

        return a.code.localeCompare(b.code);
    });

    new Chart(element, {
        type: "bar",
        data: {
            labels: entries.map((entry) => entry.code),
            datasets: [
                {
                    data: entries.map((entry) => entry.count),
                    backgroundColor: "#0d7d9a",
                },
            ],
        },
        options: {
            ...defaultBarOptions,
            scales: {
                ...defaultBarOptions.scales,
                y: {
                    ...defaultBarOptions.scales?.y,
                    type: "logarithmic",
                },
            },
            plugins: {
                ...defaultBarOptions.plugins,
                tooltip: {
                    ...defaultBarOptions.plugins.tooltip,
                    callbacks: {
                        ...defaultBarOptions.plugins.tooltip.callbacks,
                        title: (items) => countries[items[0].label as TCountryCode].name,
                    },
                },
            },
        },
    });
};

const renderAgeDistribution = (element: HTMLCanvasElement | null) => {
    if (!element) {
        return;
    }

    const entries = JSON.parse(element.dataset.statistics as string) as {
        age: number;
        count: number;
    }[];

    entries.sort((a, b) => a.age - b.age);

    new Chart(element, {
        type: "bar",
        data: {
            labels: entries.map((entry) => entry.age),
            datasets: [
                {
                    data: entries.map((age) => age.count),
                    backgroundColor: "#0d7d9a",
                },
            ],
        },
        options: defaultBarOptions,
    });
};

const renderSpeciesCategories = (element: HTMLCanvasElement | null) => {
    if (!element) {
        return;
    }

    const entries = JSON.parse(element.dataset.statistics as string) as {
        name: string;
        count: number;
    }[];

    entries.sort((a, b) => {
        const result = b.count - a.count;

        if (result !== 0) {
            return result;
        }

        return a.name.localeCompare(b.name);
    });

    new Chart(element, {
        type: "bar",
        data: {
            labels: entries.map((entry) => entry.name),
            datasets: [
                {
                    data: entries.map((age) => age.count),
                    backgroundColor: "#0d7d9a",
                },
            ],
        },
        options: defaultBarOptions,
    });
};

document.addEventListener("DOMContentLoaded", () => {
    renderTotal(document.querySelector("canvas#total"));
    renderCountryDistribution(document.querySelector("canvas#country-distribution"));
    renderAgeDistribution(document.querySelector("canvas#age-distribution"));
    renderSpeciesCategories(document.querySelector("canvas#species-categories"));
});
